import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import {
  GetWaterProfilesByAccount,
  AlterActiveState,
} from "../../services/ClientWaterProfileService";
import ViewWaterProfile from "./ViewWaterProfile";
import CommonLoader from "../common/CommonLoader";
import PageHeading from "../common/PageHeading";

import { useHistory } from "react-router-dom";

import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

const WaterProfilesTable = ({ accountNumber }) => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "accountNumber", label: "Account" },
    { id: "type", label: "Type" },
    { id: "calcium", label: "Calcium" },
    { id: "totalAlkalinity", label: "Total Alkalinity" },
    { id: "bicarbonate", label: "Bicarbonate" },
    { id: "sulfate", label: "Sulfate" },
    { id: "magnesium", label: "Magnesium" },
    { id: "totalHardness", label: "Total Hardness" },
    { id: "iron", label: "Iron" },
    { id: "pH", label: "pH Level" },
    { id: "totalChlorine", label: "Total Chlorine" },
    { id: "residualAlkalinity", label: "Residual Alkalinity" },
    { id: "chloride", label: "Chloride" },
    { id: "sulfateToChlorideRatio", label: "Sulfate/Chloride Ratio" },
    { id: "sodium", label: "Sodium" },
    { id: "zinc", label: "Zinc" },
    { id: "potassium", label: "Potassium" },
    { id: "copper", label: "Copper" },
    { id: "tds", label: "TDS" },
    { id: "actions", label: "Actions" },
  ];

  const chemicalFields = [
    "calcium",
    "totalAlkalinity",
    "bicarbonate",
    "sulfate",
    "magnesium",
    "totalHardness",
    "iron",
    "pH",
    "totalChlorine",
    "residualAlkalinity",
    "chloride",
    "sulfateToChlorideRatio",
    "sodium",
    "zinc",
    "potassium",
    "copper",
    "tds",
  ];

  const rowsPerPageOptions = [5, 10, 25, 50, 100, 200];

  const [profilesData, setProfilesData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const history = useHistory();

  useEffect(() => {
    fetchProfiles();
  }, [accountNumber]);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setPaginatedData(profilesData.slice(start, end));
  }, [profilesData, page, rowsPerPage]);

  const fetchProfiles = async () => {
    if (accountNumber == undefined) {
      return;
    }
    try {
      setLoading(true);
      const response = await GetWaterProfilesByAccount(accountNumber);
      setProfilesData(response.results || []);
      setPaginatedData(response.results || []);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      NotificationManager.error("Error fetching profiles", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (profile) => setSelectedProfile(profile);

  const handleBackClick = () => {
    setSelectedProfile(null);
  };

  const handleToggleActive = async (profileId) => {
    try {
      setLoading(true);
      const response = await AlterActiveState(profileId);
      if (response.results === true) {
        NotificationManager.success(
          "Profile status updated successfully",
          "Success"
        );
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error updating profile status", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error updating profile status", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfileClick = () => history.push("/water-profiles/add");

  const handleEditProfileClick = (profile) =>
    history.push(`/water-profiles/${profile.id}`);

  const renderActions = (profile) => (
    <div style={{ minWidth: "120px", display: "flex", gap: "4px" }}>
      <Tooltip title={profile.isActive ? "Mark Inactive" : "Mark Active"}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleToggleActive(profile.id)}
        >
          <DoneAllIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Edit Profile">
        <IconButton
          size="small"
          color="warning"
          onClick={() => handleEditProfileClick(profile)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="View Profile">
        <IconButton
          size="small"
          color="success"
          onClick={() => handleViewClick(profile)}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <Box sx={{ p: 2 }}>
      <PageHeading
        title="Water Quality Profiles"
        action={
          !selectedProfile && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={handleAddProfileClick}
            >
              New Profile
            </Button>
          )
        }
      />

      {selectedProfile ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ mb: 2 }}
          >
            Back to List
          </Button>
          <ViewWaterProfile profile={selectedProfile} />
        </>
      ) : (
        <Paper>
          <TableContainer sx={{ maxHeight: 520, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        ...(col.id === "actions" && { minWidth: 130 }),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((profile) => (
                    <TableRow key={profile.id} hover>
                      {columns.slice(0, -1).map((col) => (
                        <TableCell key={col.id} sx={{ whiteSpace: "nowrap" }}>
                          {chemicalFields.includes(col.id)
                            ? profile[col.id]?.toFixed(2) || "N/A"
                            : profile[col.id] || "N/A"}
                        </TableCell>
                      ))}
                      <TableCell>{renderActions(profile)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5, color: "text.secondary" }}
                    >
                      {loading ? "Loading profiles..." : "No water profiles found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={profilesData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      <CommonLoader loading={loading} />
    </Box>
  );
};

export default WaterProfilesTable;
